<template>
	<v-select
		ref="vselect"
		:value="value"
		:append-icon="appendIcon"
		:background-color="backgroundColor"
		:chips="chips"
		:clearable="clearable"
		:deletable-chips="deletableChips"
		:dense="dense"
		:disabled="disabled"
		:flat="flat"
		:hide-details="hideDetails"
		:hide-selected="hideSelected"
		:hint="hint"
		:item-text="itemText"
		:item-value="itemValue"
		:items="items"
		:label="label"
		:light="light"
		:loading="loading"
		:menu-props="menuProps"
		:multiple="multiple"
		:outlined="outlined"
		:placeholder="placeholder"
		:readonly="readonly"
		:required="required"
		:return-object="returnObject"
		:solo="solo"
		:small-chips="smallChips"
		v-bind="forwardedAttrs"
		v-on="other_listeners"
		@input="$emit('input', $event)"
		@change="$emit('change', $event)"
	>
		<template v-if="$scopedSlots.item" v-slot:item="data">
			<slot name="item" v-bind="data"></slot>
		</template>
		<template v-if="$scopedSlots['append-item']" v-slot:append-item>
			<slot name="append-item"></slot>
		</template>
	</v-select>
</template>

<script>
export default {
	inheritAttrs: false,
	model: {
		prop: 'value',
		event: 'input'
	},
	props: {
		value: {
			default: undefined
		},
		appendIcon: String,
		backgroundColor: String,
		chips: Boolean,
		clearable: Boolean,
		deletableChips: Boolean,
		dense: Boolean,
		disabled: Boolean,
		flat: Boolean,
		hideDetails: {
			type: [Boolean, String],
			default: 'auto'
		},
		hideSelected: Boolean,
		hint: String,
		items: Array,
		itemText: {
			type: String,
			default: 'text'
		},
		itemValue: {
			type: String,
			default: 'value'
		},
		label: String,
		light: Boolean,
		loading: [Boolean, String],
		menuProps: Object,
		multiple: Boolean,
		outlined: Boolean,
		placeholder: String,
		readonly: Boolean,
		required: Boolean,
		returnObject: Boolean,
		solo: Boolean,
		smallChips: Boolean,
		accessibleLabel: String,
	},
	computed: {
		forwardedAttrs() {
			const { 'accessible-label': accessible_label, ...rest } = this.$attrs
			if (this.resolvedAriaLabel && !rest['aria-label'] && !rest['aria-labelledby']) {
				rest['aria-label'] = this.resolvedAriaLabel
			}
			return rest
		},
		other_listeners() {
			const { input, change, ...rest } = this.$listeners
			return rest
		},
		resolvedAriaLabel() {
			return this.accessibleLabel || this.$attrs['aria-label']
		}
	},
	mounted() {
		this.sync_aria_readonly()
		this.sync_hidden_legend_accessibility()
		this.sync_input_accessibility()
		const input = this.$refs.vselect.$el.querySelector('input[type="text"]')
		if (input) {
			input.style.cursor = 'pointer'
		}
	},
	watch: {
		readonly() {
			this.sync_aria_readonly()
		}
	},
	updated() {
		this.sync_hidden_legend_accessibility()
		this.sync_input_accessibility()
	},
	methods: {
		/** We manually sync this as Vuetify 2 does not handle aria-readonly automatically */
		sync_aria_readonly() {
			const vselect = this.$refs.vselect
			if (!vselect || !vselect.$el) return

			const input = vselect.$el.querySelector('input[type="text"]')
			if (!input) return

			input.setAttribute('readonly', this.readonly)
		},
		sync_input_accessibility() {
			const vselect = this.$refs.vselect
			if (!vselect || !vselect.$el || !this.resolvedAriaLabel) return

			const input = vselect.$el.querySelector('input[type="text"], input[role="combobox"], textarea')
			if (!input) return

			input.setAttribute('aria-label', this.resolvedAriaLabel)
		},
		sync_hidden_legend_accessibility() {
			const vselect = this.$refs.vselect
			if (!vselect || !vselect.$el) return

			const hidden_legend_span = vselect.$el.querySelector('fieldset[aria-hidden="true"] legend span')
			if (!hidden_legend_span) return

			hidden_legend_span.setAttribute('aria-hidden', 'true')
			hidden_legend_span.style.opacity = '0'
			hidden_legend_span.style.color = 'transparent'
			hidden_legend_span.style.backgroundColor = 'transparent'
		}
	}
}
</script>

<style lang="scss">
.v-application {
	.v-input.v-select.v-input--is-focused {
		.v-input__slot input:focus,
		.v-input__slot input:focus-visible {
			outline: none !important;
			box-shadow: none !important;
		}

		.v-input__slot fieldset {
			border: 2px solid #1565c0 !important;
		}
	}
}
</style>
